/**
 * `buildFieldOverlayRenderer` lives in its own module so the
 * `FieldOverlayLayer` component file can be a pure components-only module
 * (Vite's fast-refresh plugin rejects mixed component + helper exports).
 *
 * The builder partitions a document's fields by anchor page index up
 * front, then returns the `renderOverlay` callback that
 * `<DocumentViewer>` calls per visible page. Keeping the bucket-by-page
 * work outside the per-page render path is what lets a 24-page form with
 * a few hundred fields stay smooth.
 */
import type { ReactNode } from 'react'

import type { Field, PatchSingleFieldBody } from '@/api/docfiller-types'

import { anchorPageIndex, type FieldAnchor } from './fieldAnchor'
import { FieldOverlayPerPage } from './FieldOverlayLayer'
import type { FieldEditingApi } from './useFieldEditing'
import type { PageModel, Rotation } from './viewer/types'

export interface BuildOverlayRendererArgs {
  fields: Field[]
  editing: FieldEditingApi
  savePatch: (
    fieldId: string,
    body: PatchSingleFieldBody,
  ) => Promise<Field>
}

export type OverlayRenderer = (args: {
  page: PageModel
  scale: number
  rotation: Rotation
  rendered: { width: number; height: number }
  scrolling: boolean
}) => ReactNode

export function buildFieldOverlayRenderer({
  fields,
  editing,
  savePatch,
}: BuildOverlayRendererArgs): OverlayRenderer {
  const byPage = new Map<number, Field[]>()
  for (const f of fields) {
    const idx = anchorPageIndex(f.anchor as FieldAnchor | null)
    if (idx == null) continue
    const arr = byPage.get(idx) ?? []
    arr.push(f)
    byPage.set(idx, arr)
  }
  return ({ page, scale, rotation, rendered, scrolling }) => {
    const pageFields = byPage.get(page.index)
    if (!pageFields || pageFields.length === 0) return null
    return (
      <FieldOverlayPerPage
        page={page}
        scale={scale}
        rotation={rotation}
        rendered={rendered}
        scrolling={scrolling}
        fields={pageFields}
        editing={editing}
        savePatch={savePatch}
      />
    )
  }
}
